import React from 'react';
import moment from 'moment';
import { BsChevronLeft, BsChevronRight } from 'react-icons/bs';
import { Oval } from 'react-loader-spinner';

import { useAppDispatch, useAppSelector } from '../../../../app/hooks';
import { getFleetSafetySummary } from '../../driversSlice';
import { TeamSafetyScoreCard } from '../TeamSafetyScoreCard/TeamSafetyScoreCard';

import './FleetSafetyTab.scss';

const formatMinutes = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const mins = Math.round(minutes % 60);
  if (hours === 0) return `${mins}m`;
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}m`;
};

export function FleetSafetyTab() {
  const dispatch = useAppDispatch();
  const [offset, setOffset] = React.useState<number>(0);

  const response = useAppSelector(state => state.drivers.fleetSafetySummary);
  const api = useAppSelector(state => state.drivers.getFleetSafetySummary);

  React.useEffect(() => {
    dispatch(getFleetSafetySummary(offset));
  }, [dispatch, offset]);

  const payPeriod = response?.payPeriod;
  const summaries = response?.summaries || [];

  // Sort a copy (never mutate the redux array) by safety score, highest first;
  // drivers with no qualifying score (null) fall to the bottom.
  const sortedSummaries = [...summaries].sort((a, b) => {
    if (a.weightedScore == null && b.weightedScore == null) return 0;
    if (a.weightedScore == null) return 1;
    if (b.weightedScore == null) return -1;
    return b.weightedScore - a.weightedScore;
  });

  const hasPrevious = !!payPeriod?.previousId;
  const hasNext = !!payPeriod?.nextId;

  const onPrevious = () => { if (hasPrevious) setOffset(o => o - 1); };
  const onNext = () => { if (hasNext) setOffset(o => o + 1); };

  const formatDates = () => {
    if (!payPeriod) return '';
    const start = moment(payPeriod.startDate);
    const end = moment(payPeriod.endDate);
    return `${start.format('MMM D')} - ${end.format('MMM D, YYYY')}`;
  };

  return (
    <div className='fleet-safety-tab'>
      <div className='header-filters-section'>
        <div className='header-row'>
          <div className='title-section'>
            <h2>Fleet Safety</h2>
            <p className='subtitle'>
              Driver safety scores for the selected pay period
            </p>
          </div>
          <div className='pay-period-picker'>
            <div className='loading-indicator'>
              {api?.loading && (
                <Oval color="#131313" height={20} width={20} secondaryColor="#FBD20A" />
              )}
            </div>
            <div
              className={`nav-button previous ${!hasPrevious ? 'disabled' : ''}`}
              onClick={onPrevious}
            >
              <BsChevronLeft />
            </div>
            <div className='pay-period-dates'>{formatDates()}</div>
            <div
              className={`nav-button next ${!hasNext ? 'disabled' : ''}`}
              onClick={onNext}
            >
              <BsChevronRight />
            </div>
          </div>
        </div>
      </div>

      <TeamSafetyScoreCard offset={offset} />

      {api?.error && (
        <div className='empty-state'>
          <div className='empty-message'>
            <h3>Couldn't load safety summaries</h3>
            <p>Something went wrong fetching this pay period. Try again.</p>
          </div>
        </div>
      )}

      {!api?.error && !api?.loading && summaries.length === 0 && (
        <div className='empty-state'>
          <div className='empty-message'>
            <h3>No drivers for this pay period</h3>
            <p>Safety scores will appear here once drivers log trips.</p>
          </div>
        </div>
      )}

      {!api?.error && summaries.length > 0 && (
        <div className='safety-table-container'>
          <div className='table-scroll-wrapper'>
            <table className='safety-table'>
              <thead>
                <tr>
                  <th>Driver</th>
                  <th>Safety score</th>
                  <th>Trips</th>
                  <th>Miles</th>
                  <th>Drive time</th>
                </tr>
              </thead>
              <tbody>
                {sortedSummaries.map(s => (
                  <tr key={s.driverId}>
                    <td>
                      <span className='driver-name'>
                        {s.firstName} {s.lastName}
                      </span>
                    </td>
                    <td>{s.weightedScore != null ? s.weightedScore : '—'}</td>
                    <td>{s.reportCount}</td>
                    <td>{s.totalDriveMiles} mi</td>
                    <td>{formatMinutes(s.totalDriveMinutes)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
